<script lang="ts">
  /**
   * The account form: one screen for signing in and for registering, on the
   * shell's `FullPage` (back dress, as the house draws a form). Two `Field`s,
   * and two equal buttons side by side that say what each does with them:
   * **Sign in** (the form's own action, so Enter does it) and **Register**.
   * There is no mode to switch.
   *
   * Reached from the profile sheet, the You tab, the Daily tab and the
   * results screen's guest prompt. It is a page laid over whatever asked for
   * it, so signing in from a result leaves the result where it was. A sign-in
   * that works closes the page.
   *
   * On a server with no accounts the page says so instead of offering a form
   * that could never work.
   */
  import { Field, FullPage, Note, PillButton } from '@artibens/app-shell';

  import { account, register as registerAccount, signIn } from '../state/account.svelte.js';

  const { open, onclose }: { open: boolean; onclose: () => void } = $props();

  let username = $state('');
  let password = $state('');

  const usernameLooksRight = $derived(/^[A-Za-z0-9_-]{3,20}$/.test(username.trim()));
  const passwordLooksRight = $derived(password.length >= 8);
  const canSubmit = $derived(usernameLooksRight && passwordLooksRight && !account.busy);

  // A refusal from the last visit is not this visit's.
  let wasOpen = false;
  $effect(() => {
    if (open && !wasOpen) {
      account.error = '';
      password = '';
    }
    wasOpen = open;
  });

  async function submit(mode: 'login' | 'register'): Promise<void> {
    if (!canSubmit) return;
    const ok =
      mode === 'login'
        ? await signIn(username.trim(), password)
        : await registerAccount(username.trim(), password);
    password = '';
    if (!ok) return;
    username = '';
    onclose();
  }
</script>

<FullPage {open} title="Account" dress="back" {onclose} testid="account-page">
  <div class="account">
    {#if account.status === 'unavailable'}
      <p class="account-blurb" data-testid="account-unavailable">
        This server is running without accounts. Everything else works exactly as it does with one.
        Your personal bests and settings are saved on this device.
      </p>
    {:else}
      <p class="account-blurb">
        An account syncs your personal bests and settings across devices and keeps a win and loss
        record. It is optional. Everything works without one.
      </p>
      <form
        class="account-form"
        data-testid="account-panel"
        onsubmit={(event) => {
          event.preventDefault();
          void submit('login');
        }}
      >
        <Field
          label="Username"
          placeholder="3 to 20 letters, digits, - or _"
          autocomplete="username"
          autocapitalize="none"
          maxlength={20}
          bind:value={username}
          testid="account-username-field"
        />
        <Field
          label="Password"
          type="password"
          placeholder="At least 8 characters"
          autocomplete="current-password"
          bind:value={password}
          testid="account-password-field"
        />
        {#if account.error}
          <Note tone="error" testid="account-error">{account.error}</Note>
        {/if}
        <!-- Two equal halves: the shell's `equal` (the same box for both
             weights since 1.3.0). -->
        <div class="account-actions">
          <PillButton
            type="submit"
            label="Sign in"
            size="lg"
            equal
            disabled={!canSubmit}
            busy={account.busy}
            testid="account-submit"
          />
          <PillButton
            variant="light"
            label="Register"
            size="lg"
            equal
            disabled={!canSubmit}
            testid="account-register"
            onclick={() => void submit('register')}
          />
        </div>
      </form>
    {/if}
  </div>
</FullPage>

<style>
  .account {
    padding: var(--ui-space-2) 0 0;
  }

  .account-blurb {
    margin: 0 0 var(--ui-space-5);
    font: var(--ui-text-label);
    font-weight: 400;
    line-height: 1.45;
    color: var(--ui-ink-2);
  }

  .account-form {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-4);
  }

  .account-actions {
    display: flex;
    gap: var(--ui-space-3);
  }
</style>
